import { useCallback } from 'react';

interface HubspotChat {
  getLoadedState: () => boolean;
  open: () => void;
  close: () => void;
  boot: () => void;
  shutdown: () => void;
  refresh: () => void;
}

const useHubspotChat = (): HubspotChat => {
  const getLoadedState = () =>
    window.HubSpotConversations?.widget.status().loaded;
  const hasLoaded = getLoadedState();

  /** Open the widget. If the widget is already open or isn't currently loaded, this is a no-op. */
  const open = useCallback(() => {
    if (hasLoaded) {
      window.HubSpotConversations?.widget.open();
    }
  }, [hasLoaded]);

  /** Close the widget. If the widget is already closed or isn't currently loaded, this is a no-op.  */
  const close = useCallback(() => {
    if (hasLoaded) {
      window.HubSpotConversations?.widget.close();
    }
  }, [hasLoaded]);

  /**
   * Load the widget for the first time on the page. If the widget is currently loaded, subsequent calls to this method are no-ops.
   * This method is only necessary if you set the loadImmediately flag to false. Otherwise, the widget will load itself automatically.
   * Note: widget.load is throttled to one call per second
   */
  const boot = useCallback(
    () => window.HubSpotConversations?.widget.load(),
    [],
  );

  /**
   * Remove the widget from the page. If the widget is not present on the page, this is a no-op.
   * To display the widget again, a full page refresh will have to occur, or one can invoke widget.load.
   */
  const shutdown = useCallback(() => {
    if (hasLoaded) {
      window.HubSpotConversations?.widget.remove();
    }
  }, [hasLoaded]);

  /**
   * Refresh and re-render the widget's information, given the current page url.
   *
   * If you house the chat widget on a single-page application, this method can be useful for refreshing the widget on route changes.
   * This allows you to specify different chatflows on different page routes.If widget.refresh is called on a route where there is no chatflow, and the user is not engaged in a conversation, the widget will be removed.
   */
  const refresh = useCallback(() => {
    if (hasLoaded) {
      window.HubSpotConversations?.widget.refresh();
    }
  }, [hasLoaded]);

  return {
    getLoadedState,
    open,
    close,
    boot,
    shutdown,
    refresh,
  };
};

export default useHubspotChat;
